/**
 * CSV → XML Tool
 *
 * Converts CSV to XML by parsing with PapaParse and serializing with fast-xml-parser.
 */

import { XMLBuilder } from 'fast-xml-parser';
import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function sanitizeXmlTagName(name: string): string {
  let tag = name.replace(/[^a-zA-Z0-9_.-]/g, '_');
  if (!/^[a-zA-Z_]/.test(tag)) tag = '_' + tag;
  tag = tag.replace(/_+/g, '_');
  return tag || '_field';
}

export const CsvToXmlTool: ITransformerTool = {
  id: 'csv-to-xml',
  name: 'CSV → XML',
  description: 'Convert CSV to XML',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'xml', 'convert', 'transform', 'tabular'],

  seo: {
    slug: 'csv-to-xml',
    pageTitle: 'CSV to XML Converter - Free Online Tool | SWE DevTools',
    metaDescription: 'Convert CSV data to XML format instantly. Supports headers, custom root and row element names. Fast, private, runs entirely in your browser.',
    keywords: ['csv to xml', 'csv to xml converter', 'convert csv to xml online', 'csv xml transformer'],
    relatedTools: ['xml-to-csv', 'csv-to-json', 'json-to-xml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.CSV],
  outputType: SEMANTIC_TYPES.XML,

  attributes: [
    {
      name: 'hasHeader',
      type: 'boolean',
      label: 'Header Row',
      defaultValue: true,
      description: 'Treat first row as column headers',
    },
    {
      name: 'rootName',
      type: 'string',
      label: 'Root Element',
      defaultValue: 'data',
      description: 'Name of the root XML element',
    },
    {
      name: 'rowName',
      type: 'string',
      label: 'Row Element',
      defaultValue: 'row',
      description: 'Name of each row XML element',
    },
    {
      name: 'pretty',
      type: 'boolean',
      label: 'Pretty Print',
      defaultValue: true,
      description: 'Indent and format the XML output',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const hasHeader = config.hasHeader !== false;
    const rootName = String(config.rootName ?? 'data').trim() || 'data';
    const rowName = String(config.rowName ?? 'row').trim() || 'row';
    const pretty = config.pretty !== false;

    let rows: Record<string, unknown>[];
    try {
      if (hasHeader) {
        const result = Papa.parse<Record<string, string>>(input, {
          header: true,
          skipEmptyLines: true,
          transform: (value) => value.trim(),
        });
        if (result.errors.length > 0) {
          throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
        }
        rows = result.data.map((row) => {
          const obj: Record<string, unknown> = {};
          const usedTags = new Set<string>();
          for (const key of Object.keys(row as Record<string, string>)) {
            let tag = sanitizeXmlTagName(key);
            // Disambiguate collisions
            if (usedTags.has(tag)) {
              let i = 2;
              while (usedTags.has(`${tag}_${i}`)) i++;
              tag = `${tag}_${i}`;
            }
            usedTags.add(tag);
            obj[tag] = (row as Record<string, string>)[key];
          }
          return obj;
        });
      } else {
        const result = Papa.parse<string[]>(input, {
          header: false,
          skipEmptyLines: true,
          transform: (value) => value.trim(),
        });
        if (result.errors.length > 0) {
          throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
        }
        rows = result.data.map((row) => {
          const obj: Record<string, unknown> = {};
          row.forEach((val, i) => {
            obj[`col${i + 1}`] = val;
          });
          return obj;
        });
      }
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const msg = e instanceof Error ? e.message : 'Invalid CSV';
      throw ToolError.validation(`Invalid CSV: ${msg}`, {
        suggestion: 'Check quotes and delimiters; ensure rows have consistent columns',
      });
    }

    try {
      const builder = new XMLBuilder({
        ignoreAttributes: false,
        format: pretty,
        indentBy: '  ',
        suppressEmptyNode: true,
      });

      const xmlObj = { [rootName]: { [rowName]: rows } };
      return builder.build(xmlObj);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to build XML';
      throw ToolError.execution(msg);
    }
  },
};
